import { useQuery, UseQueryResult, useQueryClient } from '@tanstack/react-query';
import { useMemo, useEffect } from 'react';
import { apiClient } from '../../utils/api-client';
import { FinancialMetrics, FinancialRecord, DateRange, Property } from '../../types';
import { useOptimizedQueryConfig, useFinancialDataCache } from '../useFinancialDataCache';
import { useListings } from './useListings';
import { convertListingsToProperties } from '../../utils/financial-utils';

// Query key factory
export const financialKeys = {
  all: ['financial'] as const,
  dashboard: (params: GetDashboardDataParams) => ['financial', 'dashboard', params] as const,
  metrics: (params: GetMetricsParams) => ['financial', 'metrics', params] as const,
  revenue: (params: GetRevenueDataParams) => ['financial', 'revenue', params] as const,
  expenses: (params: GetExpenseDataParams) => ['financial', 'expenses', params] as const,
  properties: () => ['financial', 'properties'] as const,
};

// API request/response types
export interface GetDashboardDataParams {
  propertyIds: string[];
  startDate: string;
  endDate: string;
  groupBy?: 'day' | 'week' | 'month';
}

export interface GetMetricsParams {
  propertyIds: string[];
  startDate: string;
  endDate: string;
  comparisonPeriod?: DateRange;
}

export interface GetRevenueDataParams {
  propertyIds: string[];
  startDate: string;
  endDate: string;
  breakdown?: 'property' | 'source' | 'time';
}

export interface GetExpenseDataParams {
  propertyIds: string[];
  startDate: string;
  endDate: string;
  categories?: string[];
}

export interface DashboardData {
  metrics: FinancialMetrics;
  revenueData: RevenueData[];
  expenseData: ExpenseData[];
  profitLossData: ProfitLossData[];
  lastUpdated: string;
}

export interface RevenueData {
  date: string;
  amount: number;
  propertyId: string;
  propertyName: string;
  source?: string;
  bookingCount?: number;
}

export interface ExpenseData {
  date: string;
  amount: number;
  category: string;
  propertyId: string;
  propertyName: string;
  description?: string;
}

export interface ProfitLossData {
  date: string;
  revenue: number;
  expenses: number;
  profit: number;
  profitMargin: number;
}

// Hook for getting dashboard data
export const useFinancialDashboard = (
  params: GetDashboardDataParams
): UseQueryResult<DashboardData, unknown> => {
  const optimizedConfig = useOptimizedQueryConfig();
  const { cacheLastViewedData } = useFinancialDataCache();
  
  const queryParams = useMemo(() => ({
    ...params,
    startDate: params.startDate,
    endDate: params.endDate,
    propertyIds: params.propertyIds.sort(), // Sort for consistent cache keys
  }), [params]);

  const query = useQuery({
    queryKey: financialKeys.dashboard(queryParams),
    queryFn: async (): Promise<DashboardData> => {
      const queryString = new URLSearchParams(queryParams as any).toString();
      const response = await apiClient.get<any>(`/financial/dashboard?${queryString}`);
      
      // Handle nested response structure: response.data.data contains the actual dashboard data
      const dashboardData = response.data?.data || response.data;
      return dashboardData;
    },
    enabled: params.propertyIds.length > 0 && !!params.startDate && !!params.endDate,
    ...optimizedConfig,
  });

  // Cache successful data only - no prefetching
  useEffect(() => {
    if (query.data && query.isSuccess) {
      cacheLastViewedData(queryParams, query.data);
    }
  }, [query.data, query.isSuccess, queryParams, cacheLastViewedData]);

  return query;
};

// Hook for getting financial metrics
export const useFinancialMetrics = (
  params: GetMetricsParams
): UseQueryResult<FinancialMetrics, Error> => {
  const optimizedConfig = useOptimizedQueryConfig();
  
  const queryParams = useMemo(() => ({
    ...params,
    propertyIds: params.propertyIds.sort(),
  }), [params]);

  return useQuery({
    queryKey: financialKeys.metrics(queryParams),
    queryFn: async (): Promise<FinancialMetrics> => {
      const queryString = new URLSearchParams(queryParams as any).toString();
      const response = await apiClient.get<FinancialMetrics>(`/financial/metrics?${queryString}`);
      return response.data;
    },
    enabled: params.propertyIds.length > 0 && !!params.startDate && !!params.endDate,
    ...optimizedConfig,
  });
};

// Hook for getting revenue data
export const useRevenueData = (
  params: GetRevenueDataParams
): UseQueryResult<RevenueData[], Error> => {
  const optimizedConfig = useOptimizedQueryConfig();
  
  const queryParams = useMemo(() => ({
    ...params,
    propertyIds: params.propertyIds.sort(),
  }), [params]);

  return useQuery({
    queryKey: financialKeys.revenue(queryParams),
    queryFn: async (): Promise<RevenueData[]> => {
      const queryString = new URLSearchParams(queryParams as any).toString();
      const response = await apiClient.get<RevenueData[]>(`/financial/revenue?${queryString}`);
      return response.data;
    },
    enabled: params.propertyIds.length > 0 && !!params.startDate && !!params.endDate,
    ...optimizedConfig,
  });
};

// Hook for getting expense data
export const useExpenseData = (
  params: GetExpenseDataParams
): UseQueryResult<ExpenseData[], Error> => {
  const optimizedConfig = useOptimizedQueryConfig();
  
  const queryParams = useMemo(() => ({
    ...params,
    propertyIds: params.propertyIds.sort(),
  }), [params]);

  return useQuery({
    queryKey: financialKeys.expenses(queryParams),
    queryFn: async (): Promise<ExpenseData[]> => {
      const queryString = new URLSearchParams(queryParams as any).toString();
      const response = await apiClient.get<ExpenseData[]>(`/financial/expenses?${queryString}`);
      return response.data;
    },
    enabled: params.propertyIds.length > 0 && !!params.startDate && !!params.endDate,
    ...optimizedConfig,
  });
};

// Hook for getting user properties (using listings API)
export const useFinancialProperties = (): UseQueryResult<Property[], Error> => {
  const listingsQuery = useListings();
  
  return useMemo(() => ({
    ...listingsQuery,
    data: listingsQuery.data ? convertListingsToProperties(listingsQuery.data) : [],
  }), [listingsQuery]) as UseQueryResult<Property[], Error>;
};

// Utility hook for invalidating financial data
export const useInvalidateFinancialData = () => {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => queryClient.invalidateQueries({ queryKey: financialKeys.all }),
    invalidateDashboard: (params?: GetDashboardDataParams) => {
      if (params) {
        queryClient.invalidateQueries({ queryKey: financialKeys.dashboard(params) });
      } else {
        queryClient.invalidateQueries({ queryKey: ['financial', 'dashboard'] });
      }
    },
    invalidateMetrics: (params?: GetMetricsParams) => {
      if (params) {
        queryClient.invalidateQueries({ queryKey: financialKeys.metrics(params) });
      } else {
        queryClient.invalidateQueries({ queryKey: ['financial', 'metrics'] });
      }
    },
    invalidateProperties: () => queryClient.invalidateQueries({ queryKey: financialKeys.properties() }),
  };
};